import type { ReactNode } from "react";
import { Inbox, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  /** "sm" for table cells and side panels. */
  size?: "sm" | "md";
  /** Render the title as an h2/h3 (when it stands in for a section's content); a <p> otherwise. */
  headingLevel?: 2 | 3;
  className?: string;
}

/** One empty / no-match state: quiet icon, title, optional description and action. */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  size = "md",
  headingLevel,
  className,
}: EmptyStateProps) {
  const Title = headingLevel === 2 ? "h2" : headingLevel === 3 ? "h3" : "p";
  return (
    <div
      data-empty-state
      className={cn(
        "flex flex-col items-center justify-center px-4 text-center",
        size === "sm" ? "py-6" : "py-10",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-canvas text-ink-3"
      >
        <Icon className="h-5 w-5" />
      </span>
      <Title className="text-emphasis text-ink">{title}</Title>
      {description ? <div className="mt-1 max-w-md text-meta text-ink-2">{description}</div> : null}
      {action ? <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  );
}
